// src/components/Dashboard/CoachDashboard/EquipmentStatusWidget.jsx
// Shows equipment status for coach's team players

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FaBox, FaCheckCircle, FaExclamationTriangle, FaArrowRight 
} from 'react-icons/fa';
import styles from './EquipmentStatusWidget.module.css';

const EquipmentStatusWidget = ({ program, refreshTrigger }) => {
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch equipment assignments
  useEffect(() => {
    const fetchAssignments = async () => {
      if (!program?._id) {
        setAssignments([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
        const response = await fetch(
          `${backendUrl}/api/equipment-assignments/program/${program._id}`
        );
        
        if (!response.ok) {
          setAssignments([]);
          return;
        }

        const data = await response.json();
        const allAssignments = data.assignments || data || [];
        setAssignments(allAssignments);
      } catch (error) {
        console.error('Error fetching equipment assignments:', error);
        setAssignments([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAssignments();
  }, [program?._id, refreshTrigger]);

  // Calculate stats
  const getStats = () => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);

    const active = assignments.filter(a => {
      const status = (a.status || '').toLowerCase();
      return status === 'active' || status === 'assigned';
    });

    const overdue = assignments.filter(a => {
      const status = (a.status || '').toLowerCase();
      if (status !== 'active' && status !== 'assigned') return false;
      if (!a.dueDate) return false;
      const dueDate = new Date(a.dueDate);
      dueDate.setHours(0, 0, 0, 0);
      return dueDate < now;
    });

    return {
      active: active.length,
      overdue: overdue.length,
      overdueItems: overdue.slice(0, 3)
    };
  };

  const stats = getStats();

  const getPlayerName = (assignment) => {
    return assignment.playerId?.firstName 
      ? `${assignment.playerId.firstName} ${assignment.playerId.lastName || ''}`.trim()
      : 'Unknown';
  };

  const getEquipmentName = (assignment) => {
    return assignment.equipmentId?.equipmentName || 'Equipment';
  };

  const getDaysOverdue = (dueDate) => {
    const now = new Date();
    const due = new Date(dueDate);
    const diff = Math.floor((now - due) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const handleViewEquipment = () => {
    if (program) {
      navigate(`/program/${program._id}/equipment`);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <FaBox className={styles.headerIcon} />
          <h3 className={styles.headerTitle}>Equipment Status</h3>
        </div>
        <div className={styles.loadingState}>
          <div className={styles.loadingSpinner}></div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <FaBox className={styles.headerIcon} />
        <h3 className={styles.headerTitle}>Equipment Status</h3>
      </div>

      <div className={styles.content}>
        {assignments.length === 0 ? (
          <div className={styles.emptyState}>
            <span className={styles.emptyIcon}>📦</span>
            <p>No equipment assigned</p>
          </div>
        ) : (
          <>
            {/* Stats Summary */}
            <div className={styles.statsSummary}>
              <div className={styles.statItem}>
                <FaCheckCircle className={`${styles.statIcon} ${styles.activeIcon}`} />
                <span className={styles.statValue}>{stats.active}</span>
                <span className={styles.statLabel}>Active</span>
              </div>
              <div className={styles.statItem}>
                <FaExclamationTriangle className={`${styles.statIcon} ${styles.overdueIcon}`} />
                <span className={`${styles.statValue} ${stats.overdue > 0 ? styles.warning : ''}`}>
                  {stats.overdue}
                </span>
                <span className={styles.statLabel}>Overdue</span>
              </div>
            </div>

            {/* Overdue Items */}
            {stats.overdue > 0 && (
              <div className={styles.overdueSection}>
                <h4 className={styles.overdueTitle}>Overdue Returns</h4>
                <div className={styles.overdueList}>
                  {stats.overdueItems.map(item => (
                    <div key={item._id} className={styles.overdueItem}>
                      <span className={styles.playerName}>{getPlayerName(item)}</span>
                      <span className={styles.equipmentName}>{getEquipmentName(item)}</span>
                      <span className={styles.daysOverdue}>
                        {getDaysOverdue(item.dueDate)} days
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <div className={styles.footer}>
        <button className={styles.footerLink} onClick={handleViewEquipment}>
          View All Equipment <FaArrowRight />
        </button>
      </div>
    </div>
  );
};

export default EquipmentStatusWidget;

